import { useState, useMemo } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  CardContent,
  Grid,
} from '@mui/material'
import {
  TableChart as ExcelIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material'
import ReportsTabs from '../../components/reports/ReportsTabs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format } from 'date-fns'
import { useArAgingReport, useGenerateArAgingExcel } from '../../hooks/api/useReports'
import { formatCurrencyWithLocale as formatCurrency } from '../../utils/formatters'
import { ArAgingCustomerRow } from '../../types'

type SortDir = 'asc' | 'desc'

const ArAgingReportPage = () => {

  const [asOfDate, setAsOfDate] = useState<Date | null>(new Date())
  const [viewReport, setViewReport] = useState(false)
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const formattedDate = asOfDate ? format(asOfDate, 'yyyy-MM-dd') : ''

  const { data, isLoading } = useArAgingReport(
    viewReport && formattedDate ? { asOfDate: formattedDate } : undefined
  )

  const excelMutation = useGenerateArAgingExcel()

  const handleViewReport = () => {
    if (asOfDate) setViewReport(true)
  }

  const handleExport = () => {
    if (formattedDate) excelMutation.mutate({ asOfDate: formattedDate })
  }

  const sortedCustomers = useMemo(() => {
    if (!data?.customers) return []
    return [...data.customers].sort((a, b) =>
      sortDir === 'desc' ? b.total - a.total : a.total - b.total
    )
  }, [data, sortDir])

  const handleSortToggle = () => {
    setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'))
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ReportIcon /> Reports
        </Typography>

        <ReportsTabs activeTab={3} />

        {/* Filter bar */}
        <Paper
          sx={{ p: 2.5, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <DatePicker
            label="As of Date"
            value={asOfDate}
            onChange={(v) => {
              setAsOfDate(v)
              setViewReport(false)
            }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="outlined"
            onClick={handleViewReport}
            disabled={!asOfDate || isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : <ReportIcon />}
          >
            View Report
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleExport}
            disabled={!asOfDate || excelMutation.isPending}
            startIcon={
              excelMutation.isPending ? <CircularProgress size={18} /> : <ExcelIcon />
            }
          >
            Export Excel
          </Button>
        </Paper>

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 6 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Generating report…
            </Typography>
          </Box>
        )}

        {/* Report */}
        {viewReport && data && !isLoading && (
          <Box>
            {/* Summary cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(
                [
                  { key: 'current', label: 'Current (0–30d)', variant: 'success' },
                  { key: '31_60', label: '31–60 Days', variant: 'info' },
                  { key: '61_90', label: '61–90 Days', variant: 'warn' },
                  { key: '90_plus', label: '90+ Days', variant: 'danger' },
                ] as const
              ).map(({ key, label, variant }) => (
                <Grid item xs={6} md={3} key={key}>
                  <Card variant="outlined">
                    <CardContent sx={{ pb: '12px !important' }}>
                      <StatusChip label={label} variant={variant} />
                      <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                        {formatCurrency(data.summary[key])}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Total */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Total Outstanding A/R
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {formatCurrency(data.summary.total)}
              </Typography>
            </Paper>

            {/* Customer table */}
            <Paper sx={{ mb: 3 }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {data.customers.length} Customer{data.customers.length !== 1 ? 's' : ''} with Outstanding Balance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  As of {format(new Date(asOfDate!), 'MMMM d, yyyy')}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f7f5f2' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        Current
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        31–60 Days
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        61–90 Days
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        90+ Days
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        <TableSortLabel
                          active
                          direction={sortDir}
                          onClick={handleSortToggle}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedCustomers.map((row: ArAgingCustomerRow) => (
                      <TableRow key={row.customer_id} hover>
                        <TableCell>{row.customer_name}</TableCell>
                        <TableCell align="right">
                          {row.current > 0 ? formatCurrency(row.current) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row['31_60'] > 0 ? formatCurrency(row['31_60']) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row['61_90'] > 0 ? formatCurrency(row['61_90']) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row['90_plus'] > 0 ? (
                            <Typography variant="body2" color="error.main" fontWeight="medium">
                              {formatCurrency(row['90_plus'])}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(row.total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* Summary footer row */}
                    <TableRow sx={{ bgcolor: '#f7f5f2', borderTop: '2px solid', borderColor: 'divider' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.summary.current)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.summary['31_60'])}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.summary['61_90'])}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.summary['90_plus'])}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(data.summary.total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}

        {/* Zero state */}
        {viewReport && !isLoading && data && data.customers.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography color="text.secondary">
              No outstanding receivables as of {formattedDate}.
            </Typography>
          </Box>
        )}

        {viewReport && !isLoading && !data && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography color="text.secondary">
              No data found for the selected date.
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default ArAgingReportPage
